import React, { useContext } from 'react';
import html2canvas from 'html2canvas';
import { renderToStaticMarkup } from 'react-dom/server';
import { ExceptionsFiltersContext } from '@app/exceptions/contexts/ExceptionsFiltersContext';
import { ExceptionsPaginationResult, BulkSpecialtyGroupException } from '@app/exceptions/interfaces';
import { SpecialtyGroupExceptionReviewStatusFilterOption, SpecialtyGroupExceptionReviewStatusFilterOptionLabels } from '@app/exceptions/specialtyGroupExceptionReviewStatusFilterOptionEnum';
import MetOverallStatus from '@app/exceptions/metOverallStatusEnum';
import UnprocessedExcludedProviders from '@app/exceptions/unprocessedExcludedProviders';
import { QESButton } from '@app/shared/qes-standards/Button';

interface ExceptionsExportProps {
  exceptionsData: ExceptionsPaginationResult<BulkSpecialtyGroupException> | null;
  isLoading: boolean;
}

const ExceptionsExport: React.FC<ExceptionsExportProps> = ({ exceptionsData, isLoading }) => {
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);

  const getActiveFilters = (): string[] => {
    const activeFilters: string[] = [];
    
    if (exceptionFilterContext.filterProjects && 
        exceptionFilterContext.filterProjects.length > 0 && 
        exceptionFilterContext.projectsOptions &&
        exceptionFilterContext.filterProjects.length < exceptionFilterContext.projectsOptions.length) {
      const projectNames = exceptionFilterContext.filterProjects.map(p => p.name || p.key).join(', ');
      activeFilters.push(`<div><strong>Projects:</strong> ${projectNames}</div>`);
    }

    if (exceptionFilterContext.filterStartDate || exceptionFilterContext.filterEndDate) {
      const startDate = exceptionFilterContext.filterStartDate || 'Not set';
      const endDate = exceptionFilterContext.filterEndDate || 'Not set';
      activeFilters.push(`<div><strong>Date Range:</strong> ${startDate} - ${endDate}</div>`);
    }

    if (exceptionFilterContext.filterReviewStatus && 
        exceptionFilterContext.filterReviewStatus !== SpecialtyGroupExceptionReviewStatusFilterOption.All) {
      const reviewStatusLabel = SpecialtyGroupExceptionReviewStatusFilterOptionLabels[exceptionFilterContext.filterReviewStatus];
      activeFilters.push(`<div><strong>Review Status:</strong> ${reviewStatusLabel}</div>`);
    }

    if (exceptionFilterContext.filterServiceArea && 
        exceptionFilterContext.filterServiceArea.length > 0 && 
        exceptionFilterContext.serviceAreaOptions) {
      
      const allServiceAreaItems: Array<{ key: string; name: string }> = [];
      exceptionFilterContext.serviceAreaOptions.forEach(parent => {
        if (parent.children) {
          parent.children.forEach(child => {
            allServiceAreaItems.push({ key: child.key, name: child.name });
          });
        }
      });

      if (exceptionFilterContext.filterServiceArea.length < allServiceAreaItems.length) {
        const selectedServiceAreas = allServiceAreaItems
          .filter(item => exceptionFilterContext.filterServiceArea.includes(item.key))
          .map(item => item.name);

        activeFilters.push(`<div><strong>Service Areas:</strong> ${selectedServiceAreas.join(', ')}</div>`);
      }
    }

    if (exceptionFilterContext.filterServiceAreaStatuses && 
        exceptionFilterContext.filterServiceAreaStatuses.length > 0 && 
        exceptionFilterContext.serviceAreaStatusOptions &&
        exceptionFilterContext.filterServiceAreaStatuses.length < exceptionFilterContext.serviceAreaStatusOptions.length) {
      const serviceAreaStatusNames = exceptionFilterContext.filterServiceAreaStatuses.map(sas => sas.name || sas.key).join(', ');
      activeFilters.push(`<div><strong>Service Area Statuses:</strong> ${serviceAreaStatusNames}</div>`);
    }

    if (exceptionFilterContext.filterSpecialty && 
        exceptionFilterContext.filterSpecialty.length > 0 && 
        exceptionFilterContext.specialtyOptions &&
        exceptionFilterContext.filterSpecialty.length < exceptionFilterContext.specialtyOptions.length) {
      const specialtyNames = exceptionFilterContext.filterSpecialty.map(sp => sp.name || sp.key).join(', ');
      activeFilters.push(`<div><strong>Specialties:</strong> ${specialtyNames}</div>`);
    }

    if (exceptionFilterContext.filterExceptionGrades && 
        exceptionFilterContext.filterExceptionGrades.length > 0 && 
        exceptionFilterContext.exceptionGradeOptions &&
        exceptionFilterContext.filterExceptionGrades.length < exceptionFilterContext.exceptionGradeOptions.length) {
      const gradeNames = exceptionFilterContext.filterExceptionGrades.map(eg => eg.name || eg.key).join(', ');
      activeFilters.push(`<div><strong>Exception Grades:</strong> ${gradeNames}</div>`);
    }

    if (exceptionFilterContext.filterExceptionStatus && 
        exceptionFilterContext.filterExceptionStatus.length > 0 && 
        exceptionFilterContext.exceptionStatusOptions &&
        exceptionFilterContext.filterExceptionStatus.length < exceptionFilterContext.exceptionStatusOptions.length) {
      const statusNames = exceptionFilterContext.filterExceptionStatus.map(es => es.name || es.key).join(', ');
      activeFilters.push(`<div><strong>Exception Status:</strong> ${statusNames}</div>`);
    }

    if (exceptionFilterContext.filterLinesOfBusiness && 
        exceptionFilterContext.filterLinesOfBusiness.length > 0 && 
        exceptionFilterContext.lineOfBusinessOptions &&
        exceptionFilterContext.filterLinesOfBusiness.length < exceptionFilterContext.lineOfBusinessOptions.length) {
      const lineNames = exceptionFilterContext.filterLinesOfBusiness.map(lb => lb.name || lb.key).join(', ');
      activeFilters.push(`<div><strong>Lines of Business:</strong> ${lineNames}</div>`);
    }

    if (exceptionFilterContext.filterGapResolutionStatuses && 
        exceptionFilterContext.filterGapResolutionStatuses.length > 0 && 
        exceptionFilterContext.gapResolutionStatusOptions &&
        exceptionFilterContext.filterGapResolutionStatuses.length < exceptionFilterContext.gapResolutionStatusOptions.length) {
      const gapStatusNames = exceptionFilterContext.filterGapResolutionStatuses.map(gs => gs.name || gs.key).join(', ');
      activeFilters.push(`<div><strong>Gap Resolution Status:</strong> ${gapStatusNames}</div>`);
    }

    if (exceptionFilterContext.metOverallStatus && 
        exceptionFilterContext.metOverallStatus !== MetOverallStatus.All) {
      activeFilters.push(`<div><strong>Met Overall Status:</strong> ${exceptionFilterContext.metOverallStatus}</div>`);
    }

    if (exceptionFilterContext.unprocessedExcludedProviders && 
        exceptionFilterContext.unprocessedExcludedProviders !== UnprocessedExcludedProviders.All) {
      activeFilters.push(`<div><strong>Excluded Providers:</strong> ${exceptionFilterContext.unprocessedExcludedProviders}</div>`);
    }

    if (exceptionFilterContext.filterTags && 
        exceptionFilterContext.filterTags.length > 0 && 
        exceptionFilterContext.tagsOptions) {
      
      const allTagItems: Array<{ key: string; name: string; groupName: string }> = [];
      exceptionFilterContext.tagsOptions.forEach(parent => {
        if (parent.children) {
          parent.children.forEach(child => {
            allTagItems.push({ 
              groupName: parent.name,
              key: child.key, 
              name: child.name
            });
          });
        }
      });

      if (exceptionFilterContext.filterTags.length < allTagItems.length) {
        const selectedTags = allTagItems
          .filter(item => exceptionFilterContext.filterTags.includes(item.key))
          .map(item => `${item.groupName} - ${item.name}`);

        activeFilters.push(`<div><strong>Tags:</strong> ${selectedTags.join(', ')}</div>`);
      }
    }

    return activeFilters;
  };

  const getTitleDiv = () => {
    const activeFilters = getActiveFilters();
    const filtersText = activeFilters.length > 0 ? activeFilters.join('') : 'No filters applied';

    const output = document.createElement('div');
    output.innerHTML = renderToStaticMarkup(
      <div className='exceptions-export__title-div'>
        <div className="exceptions-export__text-container">
          <h2 className="exceptions-export__title-text">Exception Metrics Summary</h2>
          <div className="exceptions-export__info-div">
            <span className="exceptions-export__date-text"><strong>Generated on:</strong> {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="exceptions-export__filters-text" dangerouslySetInnerHTML={{ __html: `<strong>Applied Filters:</strong> ${filtersText}` }}></span>
          </div>
        </div>
        <img className="exceptions-export__logo" src={`${window.customUIBaseUrl}/images/QuestAnalyticsLogo-R.png`} alt="Quest Analytics" role="img" />
      </div>
    );

    return output;
  };

  const exportMetrics = () => {
    const selector = '.exceptions-overview__metrics-container';
    const exportDiv = document.querySelector(selector) as HTMLElement;

    if (!exportDiv) {
      console.error('Metrics container not found');
      return;
    }

    const exportName = `Exception_Metrics_Summary_${new Date().toLocaleDateString('en-US').replace(/\//g, '_')}`;
    const titleDiv = getTitleDiv();

    html2canvas(exportDiv, {
      ignoreElements: function (element) {
        return element.getAttribute('data-html2canvas-ignore') === 'true' ||
               element.classList.contains('exceptions-overview__export-button');
      },
      onclone: (clonedDoc) => {
        const clonedExportDiv = clonedDoc.querySelector(selector) as HTMLElement;

        if (clonedExportDiv) {
          clonedExportDiv.className = 'exceptions-overview__metrics-export-container';
          
          // Create a separate grid container for metrics
          const metricsGrid = document.createElement('div');
          metricsGrid.className = 'exceptions-export__metrics-grid';
          
          const children = Array.from(clonedExportDiv.children);
          children.forEach(child => {
            metricsGrid.appendChild(child);
          });
          
          clonedExportDiv.appendChild(titleDiv);
          clonedExportDiv.appendChild(metricsGrid);
        }
      },
      removeContainer: true,
      scale: 2
    }).then(exportCanvas => {
      const imgData = exportCanvas.toDataURL("image/png");

      const link = document.createElement('a');
      link.download = exportName;
      link.href = imgData;
      link.click();
    }).catch(error => {
      console.error('Error exporting metrics:', error);
    });
  };

  return (
    <QESButton
      className="exceptions-overview__export-button"
      onClick={exportMetrics}
      disabled={isLoading || !exceptionsData}
      qesTestId="exceptions-export-button"
      qesType="primary"
    >
      Export
    </QESButton>
  );
};

export default ExceptionsExport;